import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { getJWT } from '../actions/auth';

interface ISocketContext {
    socket: Socket | null;
}

const SocketContext = createContext<ISocketContext>({ socket: null });

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
    const socketRef = useRef<Socket | null>(null);
    const app = useSelector((state: any) => state.app); // Adjust the path to where JWT is stored
    const dispatch = useDispatch();

    const getToken = async () => {
        const token = await dispatch(getJWT());
        try {
            const socket = io(process.env.FILE_URL, {
                auth: {
                    token: token,
                },
                transports: ['websocket'],
            });

            socketRef.current = socket;

            socket.on('connect', () => {
                console.log('✅ Socket connected:', socket.id);
                // Update ref when connected
                socketRef.current = socket;
            });

            socket.on('disconnect', () => {
                console.log('❌ Socket disconnected');
            });

            socket.on('connect_error', (error) => {
                console.error('Socket connection error:', error);
            });
            socket.on('connect_timeout', (error) => {
                console.error('Socket connection timeout:', error);
            })

            socket.on('error', (error) => {
                console.error('Socket error:', error);
            });
            socket.on('reconnect_attempt', (attempt) => {
                console.log('Socket reconnect attempt:', attempt);
            });

            return () => {
                socket.disconnect();
            };
        } catch (error) {
            console.error('Error getting token:', error);
        }
    }

    useEffect(() => {
        if (!app?.user) return;

        if (socketRef.current) {
            socketRef.current.disconnect(); // Disconnect existing socket before reconnect
        }
        getToken();
    }, [app?.user]); // reconnect when JWT changes

    // Update context value when socket changes
    const [socketValue, setSocketValue] = useState<Socket | null>(socketRef.current);

    useEffect(() => {
        const updateSocket = () => {
            setSocketValue(socketRef.current);
        };

        // Update socket value when ref changes
        const interval = setInterval(updateSocket, 100);

        return () => clearInterval(interval);
    }, []);

    return (
        <SocketContext.Provider value={{ socket: socketValue }}>
            {children}
        </SocketContext.Provider>
    );
};
